import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton } from "@mui/material";
import { useAppSelector } from "../../../../../../services/redux/hooks/use-selector";
import {
  selectAssistantMeta,
  selectAssistantValue,
} from "../../../../redux/selectors";
import { AppInput } from "../../../../../../shared/components/AppInput";
import { TValueForService } from "../../../../../../services/api/instances/storageApi/types/service_types";
import { useAssistantField } from "../../../../hooks/use-assistant-field";
import { getUpdatedValue } from "../../../../helpers/getUpdatedValue";
import { SparksIcon } from "../../../../../../shared/icons/SparksIcon";
import {
  getInstructionWithoutHiddenPart,
  getInstructionWithoutHiddenPartWithTrim,
} from "../../../../helpers/getInstructionWithoutHiddenPart";
import { TEntityInfo } from "../../../../../../common/types/utility";
import { TModalsIds } from "../../../../../../app/state/modalSlice/types";
import styles from "./styles.module.css";
import { rebuildSecurityBlocks } from "../../../../helpers/rebuildSecurityBlocks";
import { useStorageSelectors } from "../../../../../../app/state/storageObjectSlice/hook/use-storage-selectors";

interface Props {
  saveAssistant: (value: TValueForService<"assistants">) => void;
  handleOpenModal?: (id: TModalsIds, info: TEntityInfo | null) => void;
  hasGeneratePromptModal?: boolean;
}

export const InstructionBlock = ({
  saveAssistant,
  handleOpenModal,
  hasGeneratePromptModal,
}: Props) => {
  const assistantValue = useAppSelector(selectAssistantValue);
  const meta = useAppSelector(selectAssistantMeta);
  const { selectedId, selectedObject } = useStorageSelectors("assistants");
  const [visibleInstruction, setVisibleInstruction] = useState<string>("");
  const { t } = useTranslation();

  useEffect(() => {
    if (assistantValue?.assistant_object) {
      const cleanText = getInstructionWithoutHiddenPartWithTrim(
        assistantValue.assistant_object.instructions,
      );
      setVisibleInstruction(cleanText);
    }
  }, [selectedId, assistantValue?.assistant_object]);

  const handleChangeInstruction = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setVisibleInstruction(event.target.value);
    },
    [assistantValue?.assistant_object?.instructions],
  );

  const onInstructionChange = useCallback(() => {
    if (!assistantValue?.assistant_object) return;
    const finalInstruction = rebuildSecurityBlocks(
      visibleInstruction,
      meta?.tools?.assistants ?? null,
      meta?.tools?.skills ?? null,
    );
    const newValue = getUpdatedValue(assistantValue, {
      instructions: finalInstruction,
    });
    saveAssistant(newValue);
  }, [
    assistantValue?.assistant_object?.instructions,
    meta?.tools,
    saveAssistant,
    visibleInstruction,
  ]);

  return (
    <div className={styles["instructions-wrapper"]}>
      <AppInput
        value={visibleInstruction}
        onChange={handleChangeInstruction}
        onBlur={onInstructionChange}
        className={styles["text-area"]}
        placeholder={t("assistants.input_instructions_placeholder")}
        multiline
        rows={10}
      />
      {hasGeneratePromptModal && (
        <IconButton
          onClick={() =>
            handleOpenModal && handleOpenModal("generate_prompt_modal", null)
          }
          className={styles["improve-icon"]}
        >
          <SparksIcon />
        </IconButton>
      )}
    </div>
  );
};
